import React from 'react';
import { COLORS } from '../../constants';

const JobSection = ({ title, intro, children, className = '' }) => (
  <div className={`rounded-lg bg-gray-50 p-5 sm:p-6 ${className}`}>
    {title && <h3 className="mb-4 text-lg font-bold text-gray-900">{title}</h3>}
    {intro && <p className="mb-4 leading-relaxed text-gray-700">{intro}</p>}
    {children}
  </div>
);

const formatOpeningCount = (vacancies) => {
  if (!vacancies) {
    return '';
  }

  return `${vacancies} ${vacancies === 1 ? 'opening' : 'openings'}`;
};

const formatVacancyLabel = (vacancies) => (
  `${vacancies === 1 ? 'Vacancy' : 'Vacancies'}: ${vacancies}`
);

const JobList = ({ items }) => (
  <ul className="space-y-3">
    {items.map((item) => (
      <li key={item} className="flex items-start gap-3">
        <span className="mt-1 h-2 w-2 flex-none rounded-full" style={{ backgroundColor: COLORS.primary }} />
        <span className="leading-relaxed text-gray-700">{item}</span>
      </li>
    ))}
  </ul>
);

export const JobTitle = ({ job, isExpanded, onToggle }) => (
  <button
    type="button"
    onClick={() => onToggle(job.id)}
    className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left font-medium text-white transition-opacity hover:opacity-90 sm:px-6"
    style={{ backgroundColor: COLORS.primary }}
    aria-expanded={isExpanded}
  >
    <span>
      <span className="block text-base font-semibold sm:text-lg">{job.title}</span>
      <span className="mt-1 block text-sm text-white/90">{job.company}</span>
      {job.meta && <span className="mt-1 block text-sm text-white/75">{job.meta}</span>}
      {job.vacancies && (
        <span className="mt-2 inline-flex rounded-full bg-white/15 px-3 py-1 text-xs font-semibold text-white">
          {formatOpeningCount(job.vacancies)}
        </span>
      )}
    </span>
    <span className="text-2xl leading-none">{isExpanded ? '-' : '+'}</span>
  </button>
);

const JobTable = ({ columns = [], rows = [] }) => (
  <div className="overflow-x-auto">
    <table className="w-full min-w-[520px] border-collapse text-left text-sm">
      <thead>
        <tr>
          {columns.map((column) => (
            <th key={column} className="border border-gray-200 bg-white px-3 py-2 font-semibold text-gray-900">
              {column}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.join('-')}>
            {row.map((cell) => (
              <td key={cell} className="border border-gray-200 bg-white px-3 py-2 text-gray-700">
                {cell}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const JobDetails = ({ job, onApplyNow }) => (
  <div className="max-w-4xl space-y-6 text-gray-800">
    <div>
      {job.eyebrow && (
        <p className="mb-2 text-sm font-semibold uppercase tracking-[0.18em]" style={{ color: COLORS.primary }}>
          {job.eyebrow}
        </p>
      )}
      <h2 className="mb-4 text-2xl font-bold leading-tight text-gray-900">
        {job.heading}
      </h2>

      <p className="mb-3 text-sm font-semibold" style={{ color: COLORS.primary }}>
        {job.company}
      </p>

      {job.meta && (
        <p className="mb-4 text-sm font-medium text-gray-600">
          {job.meta}
        </p>
      )}

      {job.vacancies && (
        <p className="mb-4 text-sm font-semibold text-gray-700">
          {formatVacancyLabel(job.vacancies)}
        </p>
      )}

      {job.description && (
        <p className="text-base leading-relaxed text-gray-700">
          {job.description}
        </p>
      )}
    </div>

    {(job.sections || []).map((section) => (
      <JobSection
        key={section.title}
        title={section.title}
        intro={section.intro}
      >
        {(section.paragraphs || []).map((paragraph) => (
          <p key={paragraph} className="mb-4 leading-relaxed text-gray-700 last:mb-0">
            {paragraph}
          </p>
        ))}

        {(section.items || []).length > 0 && <JobList items={section.items} />}

        {section.table && (
          <JobTable columns={section.table.columns} rows={section.table.rows} />
        )}

        {(section.subsections || []).map((subsection) => (
          <div key={subsection.title} className="mt-5 first:mt-0">
            <h4 className="mb-3 text-base font-semibold text-gray-900">{subsection.title}</h4>
            {(subsection.paragraphs || []).map((paragraph) => (
              <p key={paragraph} className="mb-4 leading-relaxed text-gray-700 last:mb-0">
                {paragraph}
              </p>
            ))}
            {(subsection.items || []).length > 0 && <JobList items={subsection.items} />}
          </div>
        ))}
      </JobSection>
    ))}

    <JobSection className="text-center">
      {job.ctaHeading && <h3 className="mb-4 text-lg font-bold text-gray-900">{job.ctaHeading}</h3>}
      <p className="mb-5 leading-relaxed text-gray-800">
        {job.ctaText || 'If this opportunity sounds right for you, take the first step by applying now.'}
      </p>
      {job.note && (
        <p className="mb-5 text-sm italic leading-relaxed text-gray-600">
          {job.note}
        </p>
      )}
      <a
        href="#careers-application"
        onClick={() => onApplyNow?.(job)}
        className="inline-flex min-h-[44px] items-center justify-center rounded-full px-8 py-3 font-semibold text-white transition-colors hover:opacity-90"
        style={{ backgroundColor: COLORS.primary }}
      >
        Apply now
      </a>
    </JobSection>
  </div>
);

const JobListing = ({ job, isExpanded, onToggle, onApplyNow }) => {
  return (
    <div className="overflow-hidden rounded-lg border border-gray-300 bg-white">
      <JobTitle job={job} isExpanded={isExpanded} onToggle={onToggle} />

      {isExpanded && (
        <div className="border-t bg-white p-5 sm:p-8">
          <JobDetails job={job} onApplyNow={onApplyNow} />
        </div>
      )}
    </div>
  );
};

export default JobListing;
